import AlarmOffRoundedIcon from '@mui/icons-material/AlarmOffRounded'
import AlarmRoundedIcon from '@mui/icons-material/AlarmRounded'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'

import LocalStorageButtons from '@components/LocalStorageButtons'
import { FilterAutosuggest } from '@components/toolbar/FilterAutosuggest'
import FilterFromToDate from '@components/toolbar/FilterFromToDate'
import FilterGridContainer from '@components/toolbar/FilterGridContainer'
import FilterGridItem from '@components/toolbar/FilterGridItem'
import FilterKpi from '@components/toolbar/FilterKpi'
import { FilterMyList } from '@components/toolbar/FilterMyList'
import { FilterSelectGebied } from '@components/toolbar/FilterSelectGebied'
import { FilterSelectObject } from '@components/toolbar/FilterSelectObject'
import { FilterSelectString } from '@components/toolbar/FilterSelectString'
import { FilterToggleButton } from '@components/toolbar/FilterToggleButton'
import type { FilterOption } from '@models/Filter'
import type { Gebied } from '@models/Gebied'

import { useLocalFilters } from '../../../core/hooks/useLocalFilters'
import { StyledIconButton } from '../../../theme/components/StyledIconButton'
import type { Werkbak } from '../../types/Werkbak'
import { queryKey } from '../hooks/useCombiOpdrachtenSearch'
import type { CombiOpdrachtenSearchFilters as Filters } from '../types/CombiOpdrachtenSearch'

interface Props {
  onChange: (filters: Filters, removeSort?: boolean) => void
  filters: Filters
  initialFilters: Filters
  onRefresh: () => void
  onReset: () => void
  onSearchChange: any
  options: any
  werkbakken: Werkbak[]
  filterOptions: {
    aannemers: string[]
    netbeheerders: string[]
    gebieden: Gebied[]
    labels: FilterOption[]
  }
}

export const CombiOpdrachtenFilters = ({
  onChange,
  filters,
  initialFilters,
  onRefresh,
  onReset,
  onSearchChange,
  options,
  werkbakken,
  filterOptions: { aannemers, netbeheerders, gebieden, labels: labelOptions },
}: Props) => {
  const { localFilters, handleFilterChange, handleZoeken, handleReset } =
    useLocalFilters<Filters>({
      filters,
      initialFilters,
      onChange,
      onRefresh,
      onReset,
    })

  const {
    werkbak,
    gebied,
    netbeheerder,
    aannemer,
    search,
    fromDate,
    toDate,
    kpi,
    labels,
    open,
    myList,
  } = localFilters

  return (
    <FilterGridContainer>
      <FilterGridItem>
        <FilterSelectObject
          options={werkbakken}
          label="KPI"
          filterKey="werkbak"
          value={werkbak}
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectGebied
          options={gebieden}
          value={gebied}
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectString
          options={netbeheerders}
          label="Netbeheerder"
          filterKey="netbeheerder"
          value={netbeheerder}
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectString
          options={aannemers}
          label="Uitvoerende"
          filterKey="aannemer"
          value={aannemer}
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      <FilterGridItem minColumns="double">
        <FilterFromToDate
          fromDate={fromDate}
          label="Plandatum"
          toDate={toDate}
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      {labelOptions.length > 0 && (
        <FilterGridItem>
          <FilterSelectObject
            multiple
            options={labelOptions}
            label="Labels"
            filterKey="labels"
            value={labels}
            onFilterChange={handleFilterChange}
          />
        </FilterGridItem>
      )}

      <FilterGridItem>
        <FilterAutosuggest
          search={search}
          options={options}
          onFilterChange={handleFilterChange}
          onSearchChange={onSearchChange}
        />
      </FilterGridItem>

      <FilterGridItem
        minColumns="full"
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <FilterKpi kpi={kpi} onFilterChange={handleFilterChange} />
        <StyledIconButton
          icon={<CloseIcon />}
          title="Reset filters"
          onClick={handleReset}
        />
        <StyledIconButton
          icon={<SearchIcon />}
          title="Filters toepassen"
          onClick={handleZoeken}
        />

        <FilterMyList myList={myList} onFilterChange={handleFilterChange} />

        <FilterToggleButton
          filterKey="open"
          value={open}
          onFilterChange={handleFilterChange}
          activeIcon={<AlarmRoundedIcon />}
          inactiveIcon={<AlarmOffRoundedIcon />}
          activeTitle="Klik om ook afgesloten opdrachten te tonen"
          inactiveTitle="Klik om alleen open opdrachten te tonen"
        />

        <LocalStorageButtons
          name={queryKey}
          storable={localFilters}
          onClickLoad={handleFilterChange}
        />
      </FilterGridItem>
    </FilterGridContainer>
  )
}
